<template>
  <v-container class="mt-5">
    <alert />
    <v-data-table
      :headers="headers"
      :items="firmMembers"
      dense
      class="elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title class="text-h6">Firm Members</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-dialog persistent v-model="dialog" max-width="700">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                color="primary"
                dark
                class="mb-2"
                v-bind="attrs"
                v-on="on"
                @click="clearForm"
              >
                Add Firm Member
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="primary white--text">
                <span class="text-h5">
                  {{ edit ? "Edit" : "Add" }} Firm Member
                </span>
              </v-card-title>

              <v-card-text class="mt-5">
                <v-form ref="form" v-model="valid" lazy-validation>
                  <v-container>
                    <v-row dense>
                      <v-col cols="12">
                        <v-alert
                          dense
                          text
                          type="info"
                          border="left"
                          class="mb-4"
                        >
                          <div class="d-flex align-center">
                            <v-icon color="info" class="mr-2"
                              >mdi-information-outline</v-icon
                            >
                            <span class="text-body-2">
                              <strong>Quick Guide:</strong> Start typing a
                              <strong>Member Name</strong>,
                              <strong>Number</strong>, or
                              <strong>Profile ID</strong> in the search field
                              below. Select the correct member from the list to
                              automatically fill their details.
                            </span>
                          </div>
                        </v-alert>
                      </v-col>

                      <v-col cols="12">
                        <v-autocomplete
                          outlined
                          dense
                          v-model="selectedMember"
                          :items="searchResults"
                          :loading="loadingSearch"
                          :search-input.sync="search"
                          color="primary"
                          hide-no-data
                          hide-selected
                          item-text="SearchName"
                          item-value="MemberNo"
                          label="Search Member (Name / No / Profile ID)"
                          placeholder="Start typing to search..."
                          prepend-inner-icon="mdi-magnify"
                          return-object
                          clearable
                          :rules="rules.required"
                          @change="onMemberSelect"
                        >
                          <template v-slot:item="{ item }">
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ item.FirstName }} {{ item.SecondName }}
                                {{ item.LastName }}
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                No: {{ item.MemberNo }} | ID:
                                {{ item.ProfileID }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field
                          outlined
                          dense
                          v-model="formData.membersNo"
                          label="Member Number"
                          readonly
                          background-color="grey lighten-4"
                          hint="Auto-filled from search"
                          persistent-hint
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field
                          outlined
                          dense
                          v-model="formData.fullName"
                          label="Full Name"
                          readonly
                          background-color="grey lighten-4"
                          hint="Auto-filled from search"
                          persistent-hint
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="6" class="mt-4">
                        <v-text-field
                          outlined
                          dense
                          v-model="formData.position"
                          label="Position"
                          placeholder="e.g. Director, Manager"
                          :rules="rules.required"
                          required
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="6" class="mt-4">
                        <v-text-field
                          outlined
                          dense
                          v-model="formData.status"
                          label="Status"
                          readonly
                          background-color="grey lighten-4"
                          hint="Auto-filled from search"
                          persistent-hint
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-form>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="grey darken-1" @click="closeDialog">
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="!valid"
                  @click="save"
                  class="px-8"
                >
                  Save Member
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" color="blue" @click="editItem(item)">
          mdi-pencil
        </v-icon>
        <v-icon small color="red" @click="deleteItem(item)">
          mdi-delete
        </v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { helper } from "../../../../utils";
import Alert from "../../../../components/Alert";

export default {
  name: "FirmMembers",
  components: { Alert },
  data: () => ({
    dialog: false,
    edit: false,
    valid: true,
    search: null,
    selectedMember: null,
    formData: {
      membersNo: "",
      fullName: "",
      position: "",
      status: "",
    },
    headers: [
      { text: "Member No", value: "membersNo" },
      { text: "Full Name", value: "fullName" },
      { text: "Position", value: "position" },
      { text: "Status", value: "status" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    rules: {
      required: [(v) => !!v || "This field is required"],
    },
    searchTimer: null,
  }),

  watch: {
    search(val) {
      if (!val || val.length < 3) return;
      if (this.loadingSearch) return;

      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.$store.dispatch("Profile/FirmMembersStore/searchMembers", val);
      }, 500);
    },
  },

  computed: {
    firmMembers() {
      return this.$store.getters["Profile/FirmMembersStore/firmMembers"];
    },
    searchResults() {
      return this.$store.getters["Profile/FirmMembersStore/searchResults"];
    },
    loadingSearch() {
      return this.$store.getters["Profile/FirmMembersStore/loadingSearch"];
    },
  },

  mounted() {
    this.$store.dispatch("Profile/FirmMembersStore/getFirmMembers");
  },

  methods: {
    onMemberSelect(member) {
      if (member) {
        this.formData.membersNo = member.MemberNo;
        this.formData.fullName = `${member.FirstName} ${
          member.SecondName ? member.SecondName + " " : ""
        }${member.LastName}`;

        // Get status from memberInformation[0].Status
        if (
          member.memberInformation &&
          member.memberInformation.length > 0 &&
          member.memberInformation[0].Status
        ) {
          this.formData.status = member.memberInformation[0].Status;
        } else {
          this.formData.status = "N/A";
        }
      } else {
        this.formData.membersNo = "";
        this.formData.fullName = "";
        this.formData.status = "";
      }
    },

    closeDialog() {
      this.dialog = false;
      this.clearForm();
    },

    clearForm() {
      this.formData = {
        membersNo: "",
        fullName: "",
        position: "",
        status: "",
      };
      this.selectedMember = null;
      this.search = null;
      this.$store.dispatch("Profile/FirmMembersStore/searchMembers", null);
      this.edit = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    editItem(item) {
      this.formData = { ...item };
      this.edit = true;
      this.dialog = true;
    },

    deleteItem(item) {
      if (confirm("Are you sure you want to delete this member?")) {
        this.$store.dispatch("Profile/FirmMembersStore/removeFirmMember", item);
      }
    },

    save() {
      if (this.$refs.form.validate()) {
        const action = this.edit
          ? "Profile/FirmMembersStore/updateFirmMember"
          : "Profile/FirmMembersStore/addFirmMember";

        this.formData.applicationNo = this.$route?.params?.application;

        this.$store.dispatch(action, helper.prepareFormData(this.formData));
        this.closeDialog();
      }
    },
  },
};
</script>

<style scoped></style>
